import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Footer from '../../components/Footer';
import Header from '../../components/Header';
import {
  ApiProject,
  CATEGORY_LABELS,
  FEATURED_PROJECTS,
  getPlatformLinks,
  normalizeProject,
  PlatformLink,
  Project,
  ProjectScreen,
  PROJECTS_API_URL,
} from './projectData';
import './ProjectDetail.scss';

type NextProject = { id: number; title: string } | null;

type LoadState = 'loading' | 'ready' | 'notFound';

interface ScreenLayout {
  mainShot?: ProjectScreen;
  subShots: ProjectScreen[];
  mobileHero?: ProjectScreen;
  mobileShots: ProjectScreen[];
}

const LINK_LABELS: Record<PlatformLink['key'], string> = {
  web: '사이트 바로가기',
  ios: 'App Store',
  android: 'Google Play',
};

const hasAppLink = (project: Project) => Boolean(project.androidUrl || project.iosUrl);
const hasAnyLink = (project: Project) => Boolean(project.webUrl) || hasAppLink(project);

// 플랫폼 링크가 입력돼 있으면 그 값 기준으로, 없으면 platform 문구로 판단
const hasMobile = (project: Project) =>
  hasAnyLink(project) ? hasAppLink(project) : /ios|android|app|앱|모바일/i.test(project.platform);
const hasWeb = (project: Project) =>
  hasAnyLink(project)
    ? Boolean(project.webUrl)
    : !project.platform || /web|웹/i.test(project.platform);

// 웹 화면 첫 장은 Main Page, 나머지는 Sub Page / 모바일 화면 첫 장은 대표 폰, 나머지는 3열 배치
const getScreenLayout = (project: Project): ScreenLayout => {
  const hasKind = project.screens.some((screen) => screen.kind);
  const mobileOnly = hasMobile(project) && !hasWeb(project);
  const isMobile = (screen: ProjectScreen) => (hasKind ? screen.kind === 'mobile' : mobileOnly);

  const web = project.screens.filter((screen) => !isMobile(screen));
  const mobile = project.screens.filter(isMobile);
  const thumbnail: ProjectScreen | undefined = project.thumbnailUrl
    ? { imageUrl: project.thumbnailUrl, caption: '' }
    : undefined;

  return {
    mainShot: web[0] || (mobileOnly ? undefined : thumbnail),
    subShots: web.slice(1),
    mobileHero: mobile[0] || (mobileOnly ? thumbnail : undefined),
    mobileShots: mobile.slice(1),
  };
};

const MonitorMock: React.FC<{ screen: ProjectScreen; title: string }> = ({ screen, title }) => (
  <div className="pd-monitor">
    <div className="pd-monitor__body">
      <div className="pd-monitor__screen">
        <img src={screen.imageUrl} alt={screen.caption || `${title} 메인 화면`} />
      </div>
      <div className="pd-monitor__chin" />
    </div>
    <div className="pd-monitor__neck" />
    <div className="pd-monitor__base" />
  </div>
);

const PhoneFrame: React.FC<{ screen: ProjectScreen; title: string }> = ({ screen, title }) => (
  <div className="pd-phone">
    <div className="pd-phone__sc">
      <img src={screen.imageUrl} alt={screen.caption || `${title} 앱 화면`} loading="lazy" />
    </div>
  </div>
);

const ProjectDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [project, setProject] = useState<Project | null>(null);
  const [next, setNext] = useState<NextProject>(null);
  const [state, setState] = useState<LoadState>('loading');
  const [progress, setProgress] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const controller = new AbortController();
    window.scrollTo(0, 0);

    const fetchProject = async () => {
      setState('loading');

      // 클라이언트에만 있는 대표 프로젝트(음수 id)는 API에 없으므로 로컬 데이터로 바로 표시
      const featuredIndex = FEATURED_PROJECTS.findIndex((item) => String(item.id) === id);
      if (featuredIndex !== -1) {
        const featuredNext = FEATURED_PROJECTS[(featuredIndex + 1) % FEATURED_PROJECTS.length];
        setProject(FEATURED_PROJECTS[featuredIndex]);
        setNext(featuredNext.id !== FEATURED_PROJECTS[featuredIndex].id ? featuredNext : null);
        setState('ready');
        return;
      }

      try {
        const response = await fetch(`${PROJECTS_API_URL}/${id}`, { signal: controller.signal });

        if (response.status === 404) {
          setProject(null);
          setState('notFound');
          return;
        }

        if (!response.ok) {
          throw new Error(`Project API responded with ${response.status}`);
        }

        const data: ApiProject = await response.json();
        setProject(normalizeProject(data));
        setNext(data.next || null);
        setState('ready');
      } catch (error) {
        if ((error as Error).name === 'AbortError') return;

        console.error('Failed to fetch project:', error);
        setProject(null);
        setState('notFound');
      }
    };

    fetchProject();

    return () => controller.abort();
  }, [id]);

  const layout = useMemo(() => (project ? getScreenLayout(project) : null), [project]);

  const metaItems = useMemo(() => {
    if (!project) return [];

    return [
      { label: 'Client', value: project.client },
      { label: 'Industry', value: project.industry },
      { label: 'Period', value: project.period },
      { label: 'Scope', value: project.scope },
    ].filter((item) => item.value);
  }, [project]);

  // 스크롤 진행률
  useEffect(() => {
    if (state !== 'ready') return undefined;

    const onScroll = () => {
      const max = document.documentElement.scrollHeight - window.innerHeight;
      setProgress(max > 0 ? Math.min(1, window.scrollY / max) : 0);
    };

    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [state]);

  // 스크롤 등장 애니메이션
  useEffect(() => {
    if (state !== 'ready' || !rootRef.current) return undefined;

    const items = Array.from(rootRef.current.querySelectorAll<HTMLElement>('[data-reveal]'));

    if (!('IntersectionObserver' in window)) {
      items.forEach((el) => el.classList.add('is-in'));
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-in');
            observer.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.08 }
    );

    items.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [state, project]);

  const renderStatus = (message: string) => (
    <div className="pd-status">
      <p>{message}</p>
      {state === 'notFound' ? (
        <Link className="pd-btn" to="/projects">
          포트폴리오 목록으로 →
        </Link>
      ) : null}
    </div>
  );

  const renderDetail = (item: Project, screens: ScreenLayout) => {
    const platformLinks = getPlatformLinks(item);
    const heroBg = item.thumbnailUrl || screens.mainShot?.imageUrl || '';
    const heroPoints = [item.platform, item.scope].filter(Boolean);
    const paragraphs = [item.description, item.challenge, item.approach, item.result].filter(Boolean);
    const subLeft = screens.subShots.filter((_, index) => index % 2 === 0);
    const subRight = screens.subShots.filter((_, index) => index % 2 === 1);
    const mobileCols = [0, 1, 2].map((col) =>
      screens.mobileShots.filter((_, index) => index % 3 === col)
    );
    const themeStyle = item.themeColor
      ? ({ '--theme': item.themeColor } as React.CSSProperties)
      : undefined;

    return (
      <div style={themeStyle}>
        <div className="pd-progress" aria-hidden="true">
          <span style={{ width: `${progress * 100}%` }} />
        </div>

        {/* ============ HERO ============ */}
        <section className="pd-hero">
          {heroBg ? (
            <div
              className="pd-hero__bg"
              style={{ backgroundImage: `url(${heroBg})` }}
              aria-hidden="true"
            />
          ) : null}
          <div className="pd-hero__shade" aria-hidden="true" />
          <div className="pd-wrap pd-hero__in">
            <div className="pd-crumbs">
              <Link to="/projects">포트폴리오</Link>
              <span>/</span>
              <b>{CATEGORY_LABELS[item.category]}</b>
            </div>

            <h1 className="pd-hero__title" data-reveal>
              {item.title}
            </h1>
            {item.headline ? (
              <p className="pd-hero__lead" data-reveal style={{ '--d': '100ms' } as React.CSSProperties}>
                {item.headline}
              </p>
            ) : null}

            {heroPoints.length ? (
              <ul className="pd-hero__points">
                {heroPoints.map((point, index) => (
                  <li
                    key={point}
                    data-reveal
                    style={{ '--d': `${200 + index * 100}ms` } as React.CSSProperties}
                  >
                    - {point}
                  </li>
                ))}
              </ul>
            ) : null}

            {platformLinks.length ? (
              <div className="pd-hero__links" data-reveal style={{ '--d': '400ms' } as React.CSSProperties}>
                {platformLinks.map((link) => (
                  <a key={link.key} href={link.url} target="_blank" rel="noopener noreferrer">
                    {LINK_LABELS[link.key]} ↗
                  </a>
                ))}
              </div>
            ) : null}
          </div>
          <span className="pd-hero__scroll" aria-hidden="true">
            Scroll
          </span>
        </section>

        {/* ============ MAIN PAGE ============ */}
        {screens.mainShot ? (
          <section className="pd-sec pd-main">
            <div className="pd-wrap">
              <h2 className="pd-sec__title" data-reveal>
                Main Page
              </h2>
              <div className="pd-main__shot" data-reveal>
                <MonitorMock screen={screens.mainShot} title={item.title} />
              </div>
            </div>
          </section>
        ) : null}

        {/* ============ CONTENT ============ */}
        <section className="pd-band">
          <div className="pd-wrap pd-band__in">
            <h2 className="pd-sec__title" data-reveal>
              Content
            </h2>
            <div className="pd-band__body" data-reveal style={{ '--d': '150ms' } as React.CSSProperties}>
              {paragraphs.map((text) => (
                <p key={text}>{text}</p>
              ))}

              {metaItems.length ? (
                <dl className="pd-band__meta">
                  {metaItems.map((meta) => (
                    <div key={meta.label}>
                      <dt>{meta.label}</dt>
                      <dd>{meta.value}</dd>
                    </div>
                  ))}
                </dl>
              ) : null}

              {item.techStack.length ? (
                <ul className="pd-band__stack">
                  {item.techStack.map((tech) => (
                    <li key={tech}>{tech}</li>
                  ))}
                </ul>
              ) : null}
            </div>
          </div>
        </section>

        {/* ============ SUB PAGE ============ */}
        {screens.subShots.length ? (
          <section className="pd-sec pd-sub">
            <div className="pd-wrap">
              <h2 className="pd-sec__title" data-reveal>
                Sub Page
              </h2>
            </div>
            <div className="pd-sub__cols">
              {[subLeft, subRight].map((column, colIndex) => (
                <div key={colIndex} className={`pd-sub__col pd-sub__col--${colIndex ? 'right' : 'left'}`}>
                  {column.map((screen) => (
                    <figure key={screen.imageUrl} data-reveal={colIndex ? 'right' : 'left'}>
                      <img src={screen.imageUrl} alt={screen.caption || `${item.title} 화면`} loading="lazy" />
                      {screen.caption ? <figcaption>{screen.caption}</figcaption> : null}
                    </figure>
                  ))}
                </div>
              ))}
            </div>
          </section>
        ) : null}

        {/* ============ MOBILE PAGE ============ */}
        {screens.mobileHero ? (
          <section className="pd-sec pd-mobile">
            <div className="pd-wrap">
              <h2 className="pd-sec__title" data-reveal>
                Mobile Page
              </h2>
              <div className="pd-mobile__hero" data-reveal>
                <PhoneFrame screen={screens.mobileHero} title={item.title} />
              </div>
              {screens.mobileShots.length ? (
                <div className="pd-mobile__cols">
                  {mobileCols.map((column, colIndex) => (
                    <div key={colIndex} className={`pd-mobile__col pd-mobile__col--${colIndex}`}>
                      {column.map((screen, index) => (
                        <figure
                          key={screen.imageUrl}
                          data-reveal
                          style={{ '--d': `${index * 80}ms` } as React.CSSProperties}
                        >
                          <PhoneFrame screen={screen} title={item.title} />
                          {screen.caption ? <figcaption>{screen.caption}</figcaption> : null}
                        </figure>
                      ))}
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          </section>
        ) : null}

        {next ? (
          <div className="pd-next">
            <Link to={`/projects/${next.id}`} data-reveal>
              <div>
                <span className="pd-next__k">Next Project</span>
                <h3>
                  다음 프로젝트 — <span className="u">{next.title}</span>
                </h3>
              </div>
              <span className="pd-next__ar">→</span>
            </Link>
          </div>
        ) : null}

        <section className="pd-cta-wrap">
          <div className="pd-wrap">
            <div className="pd-cta" data-reveal>
              <h2>
                비슷한 문제를
                <br />
                풀고 계신가요?
                <br />
                <span className="u">이야기해 주세요.</span>
              </h2>
              <Link className="pd-btn" to="/inquiry">
                프로젝트 문의하기 →
              </Link>
            </div>
          </div>
        </section>
      </div>
    );
  };

  return (
    <div className="project-detail" ref={rootRef}>
      <Header />
      <main>
        {state === 'loading' ? renderStatus('프로젝트를 불러오는 중입니다...') : null}
        {state === 'notFound' ? renderStatus('프로젝트를 찾을 수 없습니다.') : null}
        {state === 'ready' && project && layout ? renderDetail(project, layout) : null}
      </main>
      <Footer />
    </div>
  );
};

export default ProjectDetail;
